import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Recommendation,
  RecommendationGuidance,
  RecommendationPriorityTier,
  RecommendationRiskLevel,
} from '@lizrd/contracts';
import {
  addRecommendationComment,
  getRecommendationComments,
  getRecommendationGuidance,
  getRecommendationHistory,
  getRecommendationMetrics,
  guideRecommendation,
  listMembers,
  setRecommendationAssignee,
  setRecommendationPriority,
} from '../lib/api';
import { EFFORT_META, PRIORITY_META, typeLabel, usd } from '../lib/graph-visuals';
import { effectiveTier } from './CostOptimizationBoard';
import { ProviderChip } from './recommendations/ProviderChip';
import { GuardrailBanner } from './recommendations/GuardrailBanner';
import { appliedAlternative } from '../lib/reco-compliance';
import { InlineMarkdown } from './ui/markdown-lite';
import { DiffBlock } from './ui/diff-block';
import { cn } from '../lib/utils';
import { Modal } from './ui/modal';
import { Pill } from './ui/pill';
import { Spinner } from './ui/spinner';
import { StepProgress } from './ui/step-progress';
import { type StepState } from './ui/stepper';
import { CONFIDENCE_META } from './EstimateConfidenceModal';
import { RecommendationLifecycle, type LifecycleAction } from './RecommendationLifecycle';
import {
  ArrowUp,
  CheckCircle2,
  Clock,
  Compass,
  FolderGit2,
  MessageCircle,
  Pin,
  Target,
  User,
  Users,
  Wrench,
} from 'lucide-react';

// The (cosmetic) pipeline the customer sees while guidance is prepared. We hold
// at the second-to-last stage until the request actually returns.
const STAGES = [
  'Reading resource & code context',
  'Preparing the implementation steps',
  'Verifying the change',
];

// Guidance may be regenerated once every 15 minutes — mirrors the server cooldown
// (recommendation.service `REGEN_COOLDOWN_MS`) and the resource "Explain deeper"
// flow, so the button shows a countdown instead of letting a click 429.
const REGEN_COOLDOWN_MS = 15 * 60_000;

const errMsg = (e: unknown): string =>
  e instanceof Error ? e.message : 'Could not prepare the guide.';

const RISK_META: Record<RecommendationRiskLevel, { label: string; color: string }> = {
  low: { label: 'Low risk', color: 'var(--iris)' },
  medium: { label: 'Medium risk', color: 'var(--warning)' },
  high: { label: 'High risk', color: 'var(--danger)' },
};

/**
 * The execution / guidance modal (Phase 10). It is the action hub for a
 * recommendation: a prominent savings summary, a step-by-step implementation
 * guide (auto-prepared on open when none exists), and a status-driven footer —
 * Accept/Reject when undecided, "Completed Execution" once accepted, and the
 * execution audit once completed. Only accepted recommendations are executable.
 */
export function GuidanceModal({
  rec,
  envId,
  canManage,
  aiEnabled,
  actionPending,
  onAction,
  onClose,
}: {
  rec: Recommendation;
  envId: string;
  canManage: boolean;
  aiEnabled: boolean;
  actionPending: boolean;
  onAction: (action: LifecycleAction) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState(0);
  const [altApplied, setAltApplied] = useState(false);

  const cached = useQuery({
    queryKey: ['guidance', envId, rec.id],
    queryFn: () => getRecommendationGuidance(envId, rec.id),
  });
  const gen = useMutation({
    mutationFn: (alternativeIntent?: string) =>
      guideRecommendation(envId, rec.id, alternativeIntent),
    onSuccess: (g) => {
      qc.setQueryData(['guidance', envId, rec.id], g);
      qc.invalidateQueries({ queryKey: ['recommendations', envId] });
      // Keep the detail page's guidance + the compliance page fresh too.
      qc.invalidateQueries({ queryKey: ['recommendation-guidance', envId, rec.id] });
      qc.invalidateQueries({ queryKey: ['compliance', envId] });
    },
  });

  const guidance = gen.data ?? cached.data ?? null;

  // The guidance lookup has settled with no usable guide — either it resolved to
  // null, or it errored (treated the same so a fetch failure can't dead-end).
  const noStoredGuide = !guidance && (cached.isSuccess || cached.isError);
  // Whether we should be auto-preparing a guide right now (nothing stored and the
  // user is allowed to generate). Also keeps the pipeline on screen continuously
  // so there's no empty-page flash before the effect fires.
  const willGenerate =
    noStoredGuide && canManage && aiEnabled && !gen.isError && !gen.data && !gen.isPending;

  // Auto-prepare when nothing is stored (and generation is allowed).
  useEffect(() => {
    if (willGenerate) {
      setPhase(0);
      gen.mutate(undefined);
    }
  }, [willGenerate]);

  // Walk the pipeline while the request is in flight.
  useEffect(() => {
    if (!gen.isPending) return;
    const t = setInterval(() => setPhase((p) => Math.min(p + 1, STAGES.length - 2)), 1100);
    return () => clearInterval(t);
  }, [gen.isPending]);

  // The multi-stage pipeline only shows while we're *first* preparing a guide
  // (nothing to show yet). A REGENERATE keeps the existing guide on screen —
  // regeneration must never blank out a working guide (the button carries the
  // "regenerating…" state), and a failed regenerate must never drop it.
  const generating = gen.isPending || willGenerate;
  // Once the compliant alternative is accepted (locally or persisted), the headline
  // reflects that smaller saving rather than the original change.
  const alt = appliedAlternative({
    complianceAlternativeApplied: !!rec.complianceAlternativeApplied || altApplied,
    complianceFlags: rec.complianceFlags,
  });
  const showPipeline = generating && !guidance;
  const fetchingGuide = cached.isLoading && !generating && !guidance;
  const conf = CONFIDENCE_META[rec.savingConfidence];

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="3xl"
      title={
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-bold text-bone">
            <InlineMarkdown text={rec.title} />
          </h3>
          <div className="mt-0.5 flex items-center gap-1.5">
            <ProviderChip provider={rec.provider} />
            <p className="truncate text-[0.7rem] text-muted">
              {rec.resourceName} · {typeLabel(rec.resourceType, rec.provider)}
            </p>
          </div>
        </div>
      }
      footer={
        // The state-change controls (Accept/Reject/…) are always available — they
        // must never wait on the guidance pipeline to finish generating.
        canManage ? (
          <div className="space-y-2">
            <RecommendationLifecycle
              status={rec.status}
              pending={actionPending}
              onAction={onAction}
              size="lg"
              menuPlacement="top"
            />
            {rec.status === 'completed' && rec.executedByName ? (
              <p className="text-center text-[0.65rem] text-muted">
                Executed by {rec.executedByName}
                {rec.executedAt && ` on ${new Date(rec.executedAt).toLocaleString()}`}
              </p>
            ) : rec.status === 'open' || rec.status === 'rejected' ? (
              <p className="text-center text-[0.65rem] text-muted">
                Accept to unlock execution — only accepted optimizations can be executed.
              </p>
            ) : null}
          </div>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <div className="flex justify-end">
          <Link
            to={`/domains/${envId}/recommendations/${rec.id}`}
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-md border border-iris/30 bg-iris/5 px-2.5 py-1 text-xs font-semibold text-iris transition-colors hover:bg-iris/10"
          >
            Open full details ↗
          </Link>
        </div>

        {/* Savings + confidence summary. */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {rec.category === 'reliability' ? (
            <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
              <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                Reliability action
              </div>
              <div className="mt-0.5 flex items-center gap-1 font-display text-xl font-bold text-warning">
                <ArrowUp aria-hidden className="h-5 w-5" />
                Scale up
              </div>
              <div className="mt-0.5 text-[0.6rem] text-muted">{rec.savingsBasis}</div>
            </div>
          ) : (
            <div className="rounded-md border border-iris/30 bg-iris/5 p-3">
              <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                {alt ? 'Potential savings · compliant alternative' : 'Potential savings'}
              </div>
              <div className="mt-0.5 font-display text-xl font-bold text-iris">
                {usd(alt?.saving ?? rec.estimatedMonthlySaving)}
                <span className="text-xs font-normal text-muted">/mo</span>
              </div>
              <div className="mt-0.5 text-[0.6rem] text-muted">
                {alt?.basis ?? rec.savingsBasis}
              </div>
            </div>
          )}
          <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
            <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
              Confidence
            </div>
            <div className="mt-1">
              <Pill color={conf.dot} title={conf.blurb}>
                {rec.savingConfidence === 'unknown' ? 'Calibrating' : `${conf.label} confidence`}
              </Pill>
            </div>
          </div>
          <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
            <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
              Estimated effort
            </div>
            <div className="mt-1">
              <Pill className="gap-1">
                <Wrench aria-hidden className="h-3.5 w-3.5" />
                {(EFFORT_META[rec.effortLevel]?.label ?? rec.effortLevel) + ' effort'}
              </Pill>
            </div>
            <div className="mt-1 text-[0.6rem] text-muted">
              {EFFORT_META[rec.effortLevel]?.band}
            </div>
          </div>
        </div>

        {/* Priority + owner — the interactive secondary controls (PRI / ACT). */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.7rem] text-muted">
          <PriorityControl rec={rec} envId={envId} canManage={canManage} />
          <AssigneeControl rec={rec} envId={envId} canManage={canManage} />
        </div>

        {/* The resource's operational signals — full-width, prominent (incl. peaks). */}
        <RelevantMetrics rec={rec} envId={envId} />

        {/* NIS2 guardrail — below the metrics; present only when a control would be
            weakened. Collapses to a confirmation once the alternative is accepted. */}
        <GuardrailBanner
          rec={rec}
          domainId={envId}
          compact
          canManage={canManage && aiEnabled}
          onAcceptAlternative={(intent) => {
            setPhase(0);
            gen.mutate(intent, { onSuccess: () => setAltApplied(true) });
          }}
          accepting={gen.isPending}
          applied={!!rec.complianceAlternativeApplied || altApplied}
        />

        {/* Deterministic detail — the full card, expanded. */}
        <Detail rec={rec} />

        {showPipeline ? (
          <div className="space-y-3 rounded-md border border-hairline bg-surface-2/40 p-4">
            <p className="text-sm text-haze">Preparing your step-by-step guide…</p>
            <StepProgress steps={pipelineSteps(phase, true)} />
          </div>
        ) : fetchingGuide ? (
          <div className="flex items-center gap-2 rounded-md border border-hairline bg-surface-2/40 p-4 text-sm text-muted">
            <Spinner size="sm" /> Loading guide…
          </div>
        ) : guidance ? (
          // A working guide always stays on screen — a regenerate shows its state
          // on the button, and a failed regenerate surfaces inline without ever
          // dropping the guide.
          <Guidance
            guidance={guidance}
            onRegenerate={
              canManage && aiEnabled
                ? () => {
                    setPhase(0);
                    gen.mutate(undefined);
                  }
                : undefined
            }
            regenerating={gen.isPending}
            regenError={gen.isError ? errMsg(gen.error) : null}
          />
        ) : gen.isError ? (
          // Nothing cached to fall back on — the first prepare failed.
          <div className="space-y-2 rounded-md border border-danger/30 bg-danger/5 p-4 text-sm">
            <p className="text-danger">{errMsg(gen.error)}</p>
            {canManage && aiEnabled && (
              <button
                onClick={() => {
                  setPhase(0);
                  gen.mutate(undefined);
                }}
                className="rounded-sm border border-border px-2.5 py-1 text-xs font-semibold text-bone hover:bg-surface-2"
              >
                Try again
              </button>
            )}
          </div>
        ) : !aiEnabled ? (
          <p className="rounded-md border border-hairline bg-surface-2/40 p-4 text-sm text-muted">
            Implementation guidance isn’t enabled on this server.
          </p>
        ) : canManage ? (
          <div className="space-y-3 rounded-md border border-hairline bg-surface-2/40 p-4 text-sm">
            <p className="text-haze">No guide has been prepared for this recommendation yet.</p>
            <button
              onClick={() => {
                setPhase(0);
                gen.mutate(undefined);
              }}
              className="inline-flex items-center gap-1.5 rounded-sm border border-iris/40 px-2.5 py-1.5 text-xs font-semibold text-iris transition-colors hover:bg-iris/10"
            >
              <Compass aria-hidden className="h-4 w-4" />
              Prepare the guide
            </button>
          </div>
        ) : (
          <p className="rounded-md border border-hairline bg-surface-2/40 p-4 text-sm text-muted">
            No guide has been prepared for this recommendation yet. Ask a teammate with
            recommendation access to prepare one.
          </p>
        )}

        <Comments envId={envId} recId={rec.id} canManage={canManage} />
        <History envId={envId} recId={rec.id} />
      </div>
    </Modal>
  );
}

/** The immutable lifecycle history (HIST-009) — collapsed by default. */
/** Comment thread / engineering notes on a recommendation (ACT-007). Distinct from
 * the immutable History timeline: human discussion, add-only. */
function Comments({
  envId,
  recId,
  canManage,
}: {
  envId: string;
  recId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const q = useQuery({
    queryKey: ['reco-comments', envId, recId],
    queryFn: () => getRecommendationComments(envId, recId),
  });
  const add = useMutation({
    mutationFn: (text: string) => addRecommendationComment(envId, recId, text),
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: ['reco-comments', envId, recId] });
    },
  });
  const comments = q.data?.comments ?? [];
  const trimmed = body.trim();

  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 text-xs">
      <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
        <MessageCircle aria-hidden className="h-4 w-4 text-muted" />
        <span className="font-semibold text-haze">Comments</span>
        {comments.length > 0 && <span className="text-muted">({comments.length})</span>}
      </div>
      <div className="space-y-2.5 px-3 py-2.5">
        {q.isLoading ? (
          <p className="text-muted">Loading…</p>
        ) : comments.length === 0 ? (
          <p className="text-muted">No comments yet.</p>
        ) : (
          <ul className="space-y-2">
            {comments.map((c) => (
              <li key={c.id} className="leading-relaxed">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-bone">{c.authorName}</span>
                  <span className="text-[0.65rem] text-muted">
                    {new Date(c.createdAt).toLocaleString()}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-haze">{c.body}</p>
              </li>
            ))}
          </ul>
        )}
        {canManage && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmed) add.mutate(trimmed);
            }}
            className="space-y-1.5 pt-1"
          >
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Add a comment or engineering note…"
              className="w-full rounded-sm border border-hairline bg-surface px-2 py-1.5 text-xs text-bone placeholder:text-muted"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!trimmed || add.isPending}
                className="rounded-sm bg-iris px-2.5 py-1 text-[0.7rem] font-semibold text-on-iris disabled:opacity-50"
              >
                {add.isPending ? 'Posting…' : 'Comment'}
              </button>
            </div>
            {add.isError && (
              <p className="text-danger">
                {add.error instanceof Error ? add.error.message : 'Could not post the comment.'}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

function History({ envId, recId }: { envId: string; recId: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['reco-history', envId, recId],
    queryFn: () => getRecommendationHistory(envId, recId),
    enabled: open,
  });
  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <Clock aria-hidden className="h-4 w-4 text-muted" />
        <span className="font-semibold text-haze">History</span>
        <span className="ml-auto text-muted">{open ? 'Hide ▾' : 'Show ▸'}</span>
      </button>
      {open && (
        <div className="border-t border-hairline px-3 py-2.5">
          {q.isLoading ? (
            <p className="text-muted">Loading…</p>
          ) : !q.data || q.data.events.length === 0 ? (
            <p className="text-muted">No history yet.</p>
          ) : (
            <ol className="space-y-1.5">
              {q.data.events.map((e) => (
                <li key={e.id} className="flex gap-2 leading-relaxed">
                  <span className="text-muted">{new Date(e.createdAt).toLocaleString()}</span>
                  <span className="text-haze">
                    <span className="font-semibold text-bone">{EVENT_LABEL[e.type] ?? e.type}</span>
                    {e.actorName && <span className="text-muted"> by {e.actorName}</span>}
                    {e.detail && <span className="text-muted"> — {e.detail}</span>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  accept: 'Accepted',
  reject: 'Rejected',
  complete: 'Executed',
  reopen: 'Reopened',
  validated: 'Validated',
  failed_validation: 'Validation failed',
  regressed: 'Reoccurred',
};

/** Shows the effective priority tier and — for managers — lets them override it
 * (PRI-006/007). Setting a tier pins it; "Auto" clears the override, reverting
 * to the platform-computed priority. */
function PriorityControl({
  rec,
  envId,
  canManage,
}: {
  rec: Recommendation;
  envId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (tier: RecommendationPriorityTier | null) =>
      setRecommendationPriority(envId, rec.id, tier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recommendations', envId] }),
  });
  const tier = effectiveTier(rec);
  const manual = rec.userPriorityTier != null;
  const tiers: RecommendationPriorityTier[] = ['high', 'medium', 'low'];
  return (
    <span className="flex items-center gap-1.5">
      {manual ? (
        <Pin aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <Target aria-hidden className="h-3.5 w-3.5" />
      )}
      <span>Priority:</span>
      {canManage ? (
        <span className="flex items-center gap-1">
          {tiers.map((t) => {
            const meta = PRIORITY_META[t]!;
            return (
              <button
                key={t}
                disabled={mut.isPending}
                onClick={() => mut.mutate(t)}
                className={cn(
                  'rounded-sm px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide transition-colors',
                  tier === t ? 'text-bone' : 'text-muted hover:text-haze',
                )}
                style={
                  tier === t
                    ? {
                        backgroundColor: `color-mix(in srgb, ${meta.color} 22%, transparent)`,
                        color: meta.color,
                      }
                    : undefined
                }
              >
                {meta.label}
              </button>
            );
          })}
          {manual && (
            <button
              disabled={mut.isPending}
              onClick={() => mut.mutate(null)}
              className="rounded-sm px-1.5 py-0.5 text-[0.6rem] font-semibold text-muted hover:text-haze"
              title="Clear the manual override — revert to platform priority"
            >
              Auto
            </button>
          )}
        </span>
      ) : (
        <span className="font-semibold" style={{ color: PRIORITY_META[tier]?.color }}>
          {PRIORITY_META[tier]?.label}
          {manual && ' (manual)'}
        </span>
      )}
    </span>
  );
}

/** Assign a recommendation to an owner (ACT-006). Candidate assignees are members
 * with access to this domain (org admins/managers always qualify). */
function AssigneeControl({
  rec,
  envId,
  canManage,
}: {
  rec: Recommendation;
  envId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers, enabled: canManage });
  const mut = useMutation({
    mutationFn: (assignedToId: string | null) =>
      setRecommendationAssignee(envId, rec.id, assignedToId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recommendations', envId] }),
  });
  const eligible = (members.data ?? []).filter(
    (m) => m.role === 'ADMINISTRATOR' || m.role === 'MANAGER' || m.domainIds.includes(envId),
  );

  return (
    <span className="flex items-center gap-1.5">
      {rec.assignedToId ? (
        <User aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <Users aria-hidden className="h-3.5 w-3.5" />
      )}
      <span>Owner:</span>
      {canManage ? (
        <select
          disabled={mut.isPending}
          value={rec.assignedToId ?? ''}
          onChange={(e) => mut.mutate(e.target.value || null)}
          className="rounded-sm border border-hairline bg-surface-2 px-1.5 py-0.5 text-[0.7rem] text-haze"
        >
          <option value="">Unassigned</option>
          {eligible.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name ?? m.email}
            </option>
          ))}
        </select>
      ) : (
        <span className="font-semibold text-haze">{rec.assignedToName ?? 'Unassigned'}</span>
      )}
    </span>
  );
}

/** A labelled, bordered detail box (action / why / location). */
function DetailBox({
  label,
  emphasis,
  children,
}: {
  label: string;
  emphasis?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-md border p-2.5 ${emphasis ? 'border-iris/30 bg-iris/5' : 'border-hairline bg-surface-2/40'}`}
    >
      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 text-xs leading-relaxed text-haze">{children}</div>
    </div>
  );
}

/** "Relevant metrics" — the resource's operational signals, shown full-width and
 * prominently below the header. Starts from the recommendation's curated evidence
 * (which already carries CPU/Memory/peak for utilization findings) and, when the
 * strategy didn't include a peak, appends the resource's busiest-hour peak from
 * the live node metrics — so a right-size reads as safe at peak, not just on
 * average. Loaded on demand when the modal opens (kept off the list hot path). */
function RelevantMetrics({ rec, envId }: { rec: Recommendation; envId: string }) {
  const q = useQuery({
    queryKey: ['reco-metrics', envId, rec.id],
    queryFn: () => getRecommendationMetrics(envId, rec.id),
  });

  const chips: { label: string; value: string }[] = [...rec.evidence];
  const m = q.data;
  const pct = (n: number) => `${Math.round(n)}%`;
  const alreadyHasPeak = rec.evidence.some((e) => e.label.toLowerCase().includes('peak'));
  if (m && !alreadyHasPeak) {
    if (m.peakCpuPct != null) chips.push({ label: 'Peak CPU', value: pct(m.peakCpuPct) });
    if (m.peakMemPct != null) chips.push({ label: 'Peak memory', value: pct(m.peakMemPct) });
  }

  if (chips.length === 0) return null;

  return (
    <Section title="Relevant metrics">
      {/* Boxes grow equally to fill the full card width (with a min width so they
          wrap instead of getting cramped when there are many). */}
      <div className="flex flex-wrap gap-2">
        {chips.map((e) => {
          const isPeak = e.label.toLowerCase().includes('peak');
          return (
            <div
              key={e.label}
              className={`grow basis-28 rounded-md border p-3 text-center ${isPeak ? 'border-warning/30 bg-warning/5' : 'border-hairline bg-surface-2/40'}`}
            >
              <div className="text-[0.55rem] font-semibold uppercase tracking-wide text-muted">
                {e.label}
              </div>
              <div className="mt-0.5 font-display text-lg font-bold leading-tight text-bone">
                {e.value}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/** The deterministic recommendation detail shown in the modal — the
 * recommended action, why it was flagged, the code location, and the validation
 * outcome once it exists. Mirrors what the (now compact) board card omits. */
function Detail({ rec }: { rec: Recommendation }) {
  return (
    <div className="space-y-2">
      <DetailBox label="Recommended action" emphasis>
        {rec.suggestedAction}
      </DetailBox>
      <DetailBox label="Why we flagged this">{rec.confidenceReason}</DetailBox>
      {rec.code && (
        <DetailBox label="Where to make the change">
          <span className="inline-flex items-center gap-1.5 text-bone">
            <FolderGit2 aria-hidden className="h-3.5 w-3.5" /> {rec.code.repo}
          </span>
          {rec.code.file && <span className="font-mono text-muted"> · {rec.code.file}</span>}
          {rec.code.verifiedFromState && (
            <span
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-iris/15 px-1.5 py-0.5 text-[0.6rem] font-semibold text-iris"
              title="This code↔cloud link was confirmed from your deploy tool's state — an exact, authoritative match."
            >
              ✓ Verified from state
            </span>
          )}
        </DetailBox>
      )}

      {rec.status === 'validated' && (
        <div className="rounded-md border border-iris/25 bg-iris/5 p-2.5 text-xs">
          <span className="inline-flex items-center gap-1 font-semibold text-iris">
            <CheckCircle2 aria-hidden className="h-4 w-4" />
            Validated — realized {usd(rec.realizedMonthlySaving ?? 0)}/mo
          </span>
          {rec.validationOutcome === 'partial' && rec.estimatedMonthlySaving > 0 && (
            <span className="text-muted"> (estimated {usd(rec.estimatedMonthlySaving)}/mo)</span>
          )}
        </div>
      )}
      {rec.status === 'failed_validation' && rec.validationEvidence && (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-2.5 text-xs leading-relaxed text-haze">
          <span className="font-semibold text-warning">Needs attention — </span>
          {rec.validationEvidence}
        </div>
      )}
      {rec.status === 'completed' && (
        <p className="text-[0.7rem] leading-relaxed text-muted">
          ⏳ Awaiting validation — Lizrd re-checks this every sync (typically within ~72h).
        </p>
      )}
    </div>
  );
}

function pipelineSteps(phase: number, running: boolean): { label: string; state: StepState }[] {
  return STAGES.map((label, i) => ({
    label,
    state: running ? (i < phase ? 'done' : i === phase ? 'active' : 'pending') : 'done',
  }));
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Guidance({
  guidance: g,
  onRegenerate,
  regenerating = false,
  regenError = null,
}: {
  guidance: RecommendationGuidance;
  onRegenerate?: () => void;
  /** A regenerate is in flight — keep the guide, show progress on the button. */
  regenerating?: boolean;
  /** The last regenerate failed — surfaced inline; the guide stays put. */
  regenError?: string | null;
}) {
  const risk = RISK_META[g.riskLevel];
  // Same 15-min cooldown the server enforces; disable + count down instead of
  // letting a click 429 (mirrors the resource "Explain deeper" flow).
  const cooldownRemainingMs = REGEN_COOLDOWN_MS - (Date.now() - new Date(g.generatedAt).getTime());
  const inCooldown = cooldownRemainingMs > 0;
  const cooldownMins = Math.ceil(cooldownRemainingMs / 60_000);
  const regenDisabled = regenerating || inCooldown;
  return (
    <div className="space-y-4 text-sm">
      {g.isRemoval && (
        <div className="rounded-md border border-danger/40 bg-danger/5 p-3">
          <div className="flex items-center gap-2">
            <span aria-hidden className="text-danger">
              ⚠️
            </span>
            <span className="font-display text-sm font-bold text-danger">Permanent removal</span>
          </div>
          <p className="mt-1 leading-relaxed text-haze">
            This change <span className="font-semibold text-bone">deletes a resource</span> — it
            cannot be undone in place. Treat it with extreme caution: confirm nothing depends on it
            (clients, DNS records, active connections) before you apply, and rely on version control
            to restore it if needed.
          </p>
        </div>
      )}

      <Section title="What this change is">
        <p className="leading-relaxed text-haze">
          <InlineMarkdown text={g.actionExplanation} />
        </p>
      </Section>

      <Section title="Risk">
        <div
          className="rounded-md border p-3"
          style={{ borderColor: `color-mix(in srgb, ${risk.color} 40%, transparent)` }}
        >
          <Pill color={risk.color}>{risk.label}</Pill>
          <p className="mt-1.5 leading-relaxed text-haze">
            <InlineMarkdown text={g.riskSummary} />
          </p>
        </div>
      </Section>

      <Section title="How to apply it">
        <ol className="space-y-1.5">
          {g.applySteps.map((step, i) => (
            <li key={i} className="flex gap-2 leading-relaxed text-haze">
              <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-iris text-[0.65rem] font-bold text-on-iris">
                {i + 1}
              </span>
              <span>
                <InlineMarkdown text={step} />
              </span>
            </li>
          ))}
        </ol>
      </Section>

      {g.diff && (
        <Section title="The exact change (diff)">
          <DiffBlock diff={g.diff} />
        </Section>
      )}

      <Section title="What to watch for">
        <ul className="space-y-1">
          {g.watchFor.map((w, i) => (
            <li key={i} className="flex gap-2 leading-relaxed text-haze">
              <span aria-hidden className="text-viridian">
                ✓
              </span>
              <span>
                <InlineMarkdown text={w} />
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="How to roll it back">
        <ol className="space-y-1.5">
          {g.rollback.map((step, i) => (
            <li key={i} className="flex gap-2 leading-relaxed text-haze">
              <span className="grid h-5 w-5 flex-none place-items-center rounded-full border border-border text-[0.65rem] font-bold text-muted">
                {i + 1}
              </span>
              <span>
                <InlineMarkdown text={step} />
              </span>
            </li>
          ))}
        </ol>
      </Section>

      {regenError && (
        <p className="rounded-md border border-danger/30 bg-danger/5 p-2.5 text-[0.7rem] text-danger">
          {regenError}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-hairline pt-2 text-[0.65rem] text-muted">
        <span>Prepared {new Date(g.generatedAt).toLocaleString()}</span>
        {onRegenerate && (
          <div className="flex items-center gap-2">
            {inCooldown && !regenerating && <span>Available again in {cooldownMins} min</span>}
            <button
              onClick={onRegenerate}
              disabled={regenDisabled}
              title={inCooldown ? `Regenerate available in ${cooldownMins} min` : undefined}
              className="font-semibold text-iris hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
            >
              {regenerating
                ? '↻ Regenerating…'
                : inCooldown
                  ? `↻ Regenerate in ${cooldownMins}m`
                  : '↻ Regenerate'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
